import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IncomeReceiptsService } from '../../../services/income-receipts.service';
import { ApproverService } from '../../../services/approver.service';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { ApproverModules } from '../../../interfaces/approver.interfaces';
import { switchMap, catchError, of } from 'rxjs';

@Component({
  selector: 'app-view-income-receipt',
  templateUrl: './view-income-receipt.component.html',
  styleUrl: './view-income-receipt.component.scss'
})
export class ViewIncomeReceiptComponent implements OnInit {


  public receipt: any;
  isLoading = true;
  loadError = false;

  // 🆕 Estado del modal de anulación
  showCancelModal = false;
  cancellationReason = '';
  isCancelling = false;

  constructor(
    private route: ActivatedRoute,
    private incomeReceiptsService: IncomeReceiptsService,
    private approverService: ApproverService,
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) {

  }


  ngOnInit(): void {
    this.initCurrentUserEmail();
    this.loadReceipt();
    this.checkApproverStatus();
  }

  private loadReceipt(): void {
    this.route.params.pipe(
      switchMap(params => {
        const receiptId = params['id'];
        // 🆕 includeInactive=true para poder ver comprobantes pendientes de aprobación
        return this.incomeReceiptsService.getById(receiptId, true).pipe(
          catchError(error => {
            console.error('Error loading receipt:', error);
            this.loadError = true;
            return of(null);
          })
        );
      })
    ).subscribe({
      next: (receipt) => {
        if (receipt && receipt.data) {
          this.receipt = receipt.data;
          this.isLoading = false;
          this.loadVersionHistory();
          this.loadGroupedReceipts();
        } else {
          this.isLoading = false;
          this.loadError = true;
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.isLoading = false;
        this.loadError = true;
      }
    });
  }

  // Método auxiliar para formatear montos con símbolo de moneda
  formatCurrency(amount: number, currency: any): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    });
    return formatter.format(amount);
  }

  // 🆕 Método para formatear montos con moneda específica del comprobante
  formatCurrencyValue(amount: number, symbol?: string, code?: string): string {
    if (!amount && amount !== 0) return '';

    // 🆕 Prioridad: parámetros > moneda del comprobante > COP por defecto
    const currencySymbol = symbol || this.receipt?.currencySymbol || '$';
    const currencyCode = code || this.receipt?.currencyCode || 'COP';

    const formattedAmount = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return `${currencySymbol}${formattedAmount} (${currencyCode})`;
  }

  // 🆕 Verificar si el comprobante tiene tasa de cambio internacional
  hasExchangeRate(): boolean {
    return this.receipt?.exchangeRate && this.receipt.exchangeRate !== null && this.receipt.exchangeRate !== 1;
  }

  // 🆕 Obtener el equivalente en COP si hay tasa de cambio
  getCOPEquivalent(amount: number): string {
    if (!this.hasExchangeRate() || !this.receipt?.exchangeRate) {
      return 'N/A';
    }

    const copAmount = amount * this.receipt.exchangeRate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(copAmount);
  }

  // 🆕 MÉTODOS DE IDENTIFICACIÓN BASADOS EN PayerType
  isIndividualPayment(): boolean {
    if (!this.receipt) return false;

    // Verificar primero si existe payerType (nuevo backend)
    if (this.receipt.payerType !== undefined) {
      return this.receipt.payerType === 'PARTICIPANT';
    }
    // Fallback para datos existentes (solo participant, no company)
    return this.receipt.participant !== null && !this.receipt.companyId;
  }

  isCorporatePayment(): boolean {
    if (!this.receipt) return false;

    // Verificar primero si existe payerType (nuevo backend) 
    if (this.receipt.payerType !== undefined) {
      return this.receipt.payerType === 'COMPANY';
    }
    // Fallback para datos existentes (no participant, pero puede tener company info)
    return this.receipt.participant === null;
  }

  isParticipantCorporatePayment(): boolean {
    if (!this.receipt) return false;

    // Verificar primero si existe payerType (nuevo backend)
    if (this.receipt.payerType !== undefined) {
      return this.receipt.payerType === 'PARTICIPANT_CORPORATE';
    }
    // Fallback para datos existentes (participant Y company)
    return this.receipt.participant !== null && this.receipt.companyId;
  }

  // Obtener el tipo de pago como string
  getPaymentTypeLabel(): string {
    if (this.isParticipantCorporatePayment()) return 'Participante Corporativo';
    if (this.isCorporatePayment()) return 'Corporativo';
    return 'Individual';
  }

  // Obtener información del pagador
  getPayerInfo(): string {
    if (this.receipt?.payerDisplayName) {
      return this.receipt.payerDisplayName; // 🆕 Usar el campo calculado del backend
    }

    // 🔄 FALLBACK para datos existentes
    if (this.isParticipantCorporatePayment()) {
      const participantName = `${this.receipt.participant?.firstName || ''} ${this.receipt.participant?.lastName || ''}`.trim();
      const companyName = this.receipt.companyName || this.receipt.company?.name || 'Empresa';
      const participantDoc = this.receipt.participant ? ` (${this.receipt.participant.typeDocument} ${this.receipt.participant.documentId})` : '';
      return `${participantName}${participantDoc} - Empresa: ${companyName}`;
    } else if (this.isCorporatePayment()) {
      const companyName = this.receipt.companyName || this.receipt.company?.name || 'Empresa';
      return `Pago Empresarial - ${companyName}`;
    } else {
      const p = this.receipt.participant;
      return `${p.firstName} ${p.lastName} (${p.typeDocument} ${p.documentId})`;
    }
  }

  isDownloadingPdf = false;

  showApprovalDialog = false;
  approvalAction = '';
  approvalNotes = '';
  isProcessingApproval = false;
  currentUserEmail = '';
  private _isApprover = false;

  versionHistory: any[] = [];
  isLoadingVersions = false;

  // 🆕 Comprobantes que comparten el mismo PaymentGroupId (pago de varias cuotas)
  groupedReceipts: any[] = [];
  isLoadingGroup = false;
  showDiffView = false;
  diffFields: { label: string; field: string; type: string }[] = [
    { label: 'Monto Bruto', field: 'grossAmount', type: 'currency' },
    { label: 'Monto Neto', field: 'netAmount', type: 'currency' },
    { label: 'Método de Pago', field: 'paymentMethod.name', type: 'text' },
    { label: 'Banco', field: 'bank.bankName', type: 'text' },
    { label: 'Concepto', field: 'concept', type: 'text' },
    { label: 'Proyecto', field: 'project.name', type: 'text' },
    { label: 'Fecha de Pago', field: 'transactionDate', type: 'date' },
    { label: 'Categoría de Ingreso', field: 'projectBudgetId', type: 'text' },
    { label: 'Observaciones', field: 'observations', type: 'text' },
    { label: 'Referencia', field: 'referenceNumber', type: 'text' },
    { label: 'Total Descuentos', field: 'totalFees', type: 'currency' },
  ];
  diffCompareFrom: any = null;
  diffCompareTo: any = null;
  selectedVersionFromId: number | null = null;
  selectedVersionToId: number | null = null;

  private initCurrentUserEmail(): void {
    const user = this.authService.getCurrentUser();
    if (user?.email) {
      this.currentUserEmail = user.email;
    }
  }

  private checkApproverStatus(): void {
    if (!this.currentUserEmail) return;

    this.approverService.checkApprover(this.currentUserEmail, ApproverModules.INCOME_RECEIPTS)
      .subscribe({
        next: (response: any) => {
          this._isApprover = response?.isApprover === true;
        },
        error: () => {
          this._isApprover = false;
        }
      });
  }

  isApprover(): boolean {
    return this._isApprover;
  }

  /**
   * Determina si el comprobante está realmente anulado/cancelado.
   * isActive === false NO implica anulación: los comprobantes editados
   * pendientes de aprobación tienen isActive = false hasta ser aprobados.
   */
  isReceiptVoided(): boolean {
    return this.receipt?.isCancelled === true || this.receipt?.status === 'CANCELED';
  }

  hasPendingVersion(): boolean {
    return this.receipt?.approvalStatus === 'PendingApproval';
  }

  getApprovalStatusBadge(): { label: string; severity: string; icon: string } {
    if (this.isReceiptVoided()) {
      return { label: 'Anulado', severity: 'danger', icon: 'pi pi-ban' };
    }

    switch (this.receipt?.approvalStatus) {
      case 'PendingApproval':
        return { label: 'Pendiente de Aprobación', severity: 'warning', icon: 'pi pi-clock' };
      case 'Approved':
        return { label: 'Aprobado', severity: 'success', icon: 'pi pi-check-circle' };
      case 'Rejected':
        return { label: 'Rechazado', severity: 'danger', icon: 'pi pi-times-circle' };
      default:
        return { label: 'Activo', severity: 'info', icon: 'pi pi-check' };
    }
  }

  /**
   * Navega al formulario de edición del comprobante
   */
  editReceipt(): void {
    if (!this.receipt) return;
    this.router.navigate(['/income-receipts/new'], {
      queryParams: { editFromId: this.receipt.id }
    });
  }

  /**
   * Abre el diálogo de aprobación/rechazo
   */
  openApprovalDialog(action: string): void {
    this.approvalAction = action;
    this.approvalNotes = '';
    this.showApprovalDialog = true;
  }

  /**
   * Procesa la aprobación o rechazo
   */
  processApproval(): void {
    if (!this.receipt || this.isProcessingApproval) return;

    this.isProcessingApproval = true;
    this.incomeReceiptsService.processApproval(
      this.receipt.id,
      this.approvalAction,
      this.approvalNotes
    ).subscribe({
      next: () => {
        this.isProcessingApproval = false;
        this.showApprovalDialog = false;
        // Recargar el comprobante
        this.loadReceipt();
      },
      error: (error: any) => {
        console.error('Error processing approval:', error);
        this.isProcessingApproval = false;
      }
    });
  }

  /**
   * Verifica si el comprobante puede ser editado.
   * Bloquea la edición si CUALQUIER versión de la cadena está PendingApproval
   * para evitar crear dos versiones pendientes en paralelo (que terminarían
   * dejando dos comprobantes activos al aprobarse).
   */
  canEdit(): boolean {
    if (!this.receipt || this.isReceiptVoided()) return false;
    if (this.receipt.approvalStatus === 'PendingApproval') return false;
    if (this.versionHistory?.some((v: any) => v.approvalStatus === 'PendingApproval')) return false;
    return true;
  }

  /**
   * Indica si hay una versión pendiente de aprobación en la cadena (para mostrar aviso en UI).
   */
  hasPendingVersionInChain(): boolean {
    return !!this.versionHistory?.some((v: any) => v.approvalStatus === 'PendingApproval');
  }

  downloadPdf(): void {
    if (this.isDownloadingPdf || !this.receipt) return;

    this.isDownloadingPdf = true;
    this.incomeReceiptsService.downloadPdf(this.receipt.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ComprobanteIngreso_${this.receipt.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPdf = false;
      },
      error: (error) => {
        console.error('Error downloading PDF:', error);
        this.isDownloadingPdf = false;
      }
    });
  }

  // 🆕 ====== Anulación de comprobante ======

  /**
   * Indica si el comprobante puede anularse desde esta vista:
   * - No está ya anulado.
   * - No está conciliado bancariamente (la conciliación lo bloquea).
   * - No está pendiente de aprobación.
   * El backend valida adicionalmente el permiso `INCOME_CANCEL`.
   */
  canCancel(): boolean {
    if (!this.receipt) return false;
    if (this.isReceiptVoided()) return false;
    if (this.receipt.isBankReconciled === true) return false;
    if (this.receipt.approvalStatus === 'PendingApproval') return false;
    return true;
  }

  openCancelModal(): void {
    if (!this.canCancel()) return;
    this.cancellationReason = '';
    this.showCancelModal = true;
  }

  closeCancelModal(): void {
    if (this.isCancelling) return;
    this.showCancelModal = false;
    this.cancellationReason = '';
  }

  confirmCancellation(): void {
    if (!this.receipt || this.isCancelling) return;

    const reason = (this.cancellationReason || '').trim();
    if (!reason) {
      this.toastService.showToast('error', 'Debe especificar el motivo de anulación');
      return;
    }
    if (reason.length < 10) {
      this.toastService.showToast('error', 'El motivo debe tener al menos 10 caracteres');
      return;
    }

    this.isCancelling = true;
    this.loadingService.show();

    this.incomeReceiptsService.cancelIncomeReceipt(this.receipt.id, reason).subscribe({
      next: () => {
        this.toastService.showToast('success', '✅ Comprobante anulado exitosamente');
        this.showCancelModal = false;
        this.cancellationReason = '';
        this.loadReceipt(); // refresca estado (badge ANULADO, etc.)
      },
      error: (error: any) => {
        console.error('Error al anular comprobante:', error);
        const errorMessage = error?.error?.errorMessage
          || (typeof error?.error === 'string' ? error.error : null)
          || 'Error al anular el comprobante';
        this.toastService.showToast('error', errorMessage);
      },
      complete: () => {
        this.isCancelling = false;
        this.loadingService.hide();
      }
    });
  }

  // --- Historial de versiones ---

  /**
   * 🆕 Carga los comprobantes hermanos del mismo PaymentGroup (cuotas cubiertas
   * por un único pago). El valor original transferido = Σ ReceivedAmount del grupo
   * (vive en el comprobante principal; los demás llevan 0).
   */
  private loadGroupedReceipts(): void {
    this.groupedReceipts = [];
    const groupId = this.receipt?.paymentGroupId;
    if (!groupId) return;
    this.isLoadingGroup = true;
    this.incomeReceiptsService.getByPaymentGroup(groupId).subscribe({
      next: (response) => {
        this.groupedReceipts = response?.data || [];
        this.isLoadingGroup = false;
      },
      error: () => {
        this.groupedReceipts = [];
        this.isLoadingGroup = false;
      }
    });
  }

  /** 🆕 El pago cubrió más de un comprobante (varias cuotas con un solo pago). */
  hasPaymentGroup(): boolean {
    return this.groupedReceipts.length > 1;
  }

  /**
   * 🆕 Comprobantes del grupo que cuentan para los montos monetarios: se excluyen
   * los anulados (igual que el reporte bancario, que filtra !IsCancelled). Los
   * anulados se siguen mostrando en la tabla, pero no suman a recibido/descuento.
   */
  private activeGroupedReceipts(): any[] {
    return this.groupedReceipts.filter(r => !r.isCancelled);
  }

  /** 🆕 Valor original transferido: Σ ReceivedAmount del grupo activo (incluye excedentes). */
  getOriginalReceivedAmount(): number {
    const active = this.activeGroupedReceipts();
    if (active.length) {
      return active.reduce((sum, r) => sum + (r.receivedAmount || 0), 0);
    }
    return this.receipt?.receivedAmount || 0;
  }

  /** 🆕 Descuento original total del pago: Σ TotalFees del grupo activo (excluye anulados). */
  getOriginalDiscount(): number {
    const active = this.activeGroupedReceipts();
    if (active.length) {
      return active.reduce((sum, r) => sum + (r.totalFees || 0), 0);
    }
    return this.receipt?.totalFees || 0;
  }

  /**
   * 🆕 Valor real que debió entrar al banco = Σ ReceivedAmount − Σ TotalFees del grupo.
   * Es el monto que usa el reporte bancario (resta TODOS los fees, no solo el de la 1ª cuota).
   */
  getBankNetAmount(): number {
    return this.getOriginalReceivedAmount() - this.getOriginalDiscount();
  }

  private loadVersionHistory(): void {
    if (!this.receipt) return;
    this.isLoadingVersions = true;
    this.incomeReceiptsService.getVersionHistory(this.receipt.id).subscribe({
      next: (response) => {
        this.versionHistory = response?.data || [];
        this.isLoadingVersions = false;
      },
      error: () => {
        this.versionHistory = [];
        this.isLoadingVersions = false;
      }
    });
  }

  hasMultipleVersions(): boolean {
    return this.versionHistory.length > 1;
  }

  isSuperseded(): boolean {
    if (!this.receipt) return false;
    return this.receipt.isActive === false
      && !this.receipt.isCancelled
      && this.receipt.approvalStatus !== 'PendingApproval'
      && this.receipt.approvalStatus !== 'Rejected';
  }

  getNewerVersion(): any | null {
    if (!this.receipt || this.versionHistory.length < 2) return null;
    return this.versionHistory.find(
      (v: any) => v.version > this.receipt.version && (v.isActive || v.approvalStatus === 'PendingApproval')
    );
  }

  navigateToVersion(receiptId: number): void {
    this.router.navigate(['/dashboard/income-receipts/view', receiptId]);
  }

  getVersionStatusLabel(version: any): string {
    if (version.isCancelled) return 'Anulado';
    if (version.isActive && version.approvalStatus === 'Approved') return 'Activo (Aprobado)';
    if (version.isActive && version.approvalStatus === 'Published') return 'Activo';
    if (version.approvalStatus === 'PendingApproval') return 'Pendiente de Aprobación';
    if (version.approvalStatus === 'Rejected') return 'Rechazado';
    if (!version.isActive && version.isDeleted) return 'Reemplazado';
    if (!version.isActive) return 'Inactivo';
    return version.approvalStatus || 'Desconocido';
  }

  getVersionStatusClass(version: any): string {
    if (version.isCancelled) return 'bg-red-100 text-red-800';
    if (version.isActive) return 'bg-green-100 text-green-800';
    if (version.approvalStatus === 'PendingApproval') return 'bg-yellow-100 text-yellow-800';
    if (version.approvalStatus === 'Rejected') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }

  // Excedentes generados por este comprobante

  hasExcessPayments(): boolean {
    return !!this.receipt?.excessPayments?.length;
  }

  getExcessStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'available':  return 'bg-green-100 text-green-800 border border-green-200';
      case 'processing': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'refunded':   return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'expired':    return 'bg-red-100 text-red-800 border border-red-200';
      default:           return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  }

  // --- Comparación Diff ---

  openDiffView(fromVersion: any, toVersion: any): void {
    this.diffCompareFrom = fromVersion;
    this.diffCompareTo = toVersion;
    this.showDiffView = true;
  }

  openDiffFromSelectors(): void {
    if (!this.selectedVersionFromId || !this.selectedVersionToId) return;
    const from = this.versionHistory.find((v: any) => v.id === this.selectedVersionFromId);
    const to = this.versionHistory.find((v: any) => v.id === this.selectedVersionToId);
    if (from && to) {
      this.openDiffView(from, to);
    }
  }

  canCompareSelected(): boolean {
    return !!this.selectedVersionFromId
      && !!this.selectedVersionToId
      && this.selectedVersionFromId !== this.selectedVersionToId;
  }

  getVersionsExcluding(excludeId: number | null): any[] {
    if (!excludeId) return this.versionHistory;
    return this.versionHistory.filter((v: any) => v.id !== excludeId);
  }

  closeDiffView(): void {
    this.showDiffView = false;
    this.diffCompareFrom = null;
    this.diffCompareTo = null;
  }

  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((o, key) => o?.[key], obj);
  }

  hasFieldChanged(field: string): boolean {
    if (!this.diffCompareFrom || !this.diffCompareTo) return false;
    const fromVal = this.getNestedValue(this.diffCompareFrom, field);
    const toVal = this.getNestedValue(this.diffCompareTo, field);
    return JSON.stringify(fromVal) !== JSON.stringify(toVal);
  }

  formatDiffValue(value: any, type: string): string {
    if (value === null || value === undefined || value === '') return '(vacío)';
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value);
      case 'date':
        return new Date(value).toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
      default:
        return String(value);
    }
  }

  getDiffChangedCount(): number {
    return this.diffFields.filter(f => this.hasFieldChanged(f.field)).length;
  }

}
